export type DashboardAssistantNavigationTarget = {
  name: string;
  description: string;
  examples: readonly string[];
  path: string;
  permission?: string;
};

// Shared source of truth for navigation tools used by both the browser
// rollback runtime and the asynchronous Inngest worker.
export const dashboardAssistantNavigationTargets = [
  {
    name: 'open_dashboard',
    description: 'Open the dashboard overview page with top-level performance metrics.',
    examples: ['open the dashboard', 'show dashboard overview', 'go to analytics home'],
    path: '/dashboard',
  },
  {
    name: 'open_home',
    description: 'Open the dashboard home page.',
    examples: ['go home', 'open the home page', 'take me to the main page'],
    path: '/',
  },
  {
    name: 'open_payments',
    description: 'Open the payments workspace for transactions and donor payments.',
    examples: ['open payments', 'show transactions', 'go to donations'],
    path: '/payments',
    permission: 'org:payments:view_payments',
  },
  {
    name: 'open_contacts',
    description: 'Open the contacts workspace for donor records and contact history.',
    examples: ['open contacts', 'show donors', 'go to donor records'],
    path: '/contacts',
    permission: 'org:contacts:view_contacts',
  },
  {
    name: 'open_subscriptions',
    description: 'Open the subscriptions workspace for recurring donations.',
    examples: ['open subscriptions', 'show recurring donations', 'go to subscription management'],
    path: '/subscriptions',
    permission: 'org:subscriptions:view_subscriptions',
  },
  {
    name: 'open_forms',
    description: 'Open the forms workspace for fundraisers, events, and donation forms.',
    examples: ['open forms', 'show fundraisers', 'go to donation forms'],
    path: '/forms',
    permission: 'org:forms:view_forms',
  },
  {
    name: 'open_elements',
    description: 'Open the elements workspace for embeddable donation elements.',
    examples: ['open elements', 'show donation widgets', 'go to embed elements'],
    path: '/elements',
    permission: 'org:elements:view_elements',
  },
  {
    name: 'open_payouts',
    description:
      'Open the payouts workspace for transfers and payout history. Use get_latest_payout instead when the user asks for payout details.',
    examples: ['open payouts', 'show transfers', 'go to payout history'],
    path: '/payouts',
    permission: 'org:payouts:view_payouts',
  },
  {
    name: 'open_settings',
    description: 'Open organization account settings.',
    examples: ['open settings', 'show configuration', 'go to account settings'],
    path: '/settings/organization/account',
    permission: 'org:settings:manage_organization_settings',
  },
  {
    name: 'open_create_contact',
    description: 'Open the create contact workspace.',
    examples: ['create a contact', 'add a donor', 'open new contact'],
    path: '/contacts/new',
    permission: 'org:contacts:process_contacts',
  },
  {
    name: 'open_create_payment',
    description: 'Open the create payment workspace.',
    examples: ['create a payment', 'process a donation', 'add a payment'],
    path: '/payments/new',
    permission: 'org:payments:process_payments',
  },
  {
    name: 'open_create_form',
    description: 'Open the create form workspace for a new fundraiser or donation form.',
    examples: ['create a form', 'make a fundraiser', 'add a donation form'],
    path: '/forms/new',
    permission: 'org:forms:process_forms',
  },
  {
    name: 'open_create_event_form',
    description: 'Open the create event form workspace.',
    examples: ['create an event', 'make an event form', 'add event registration'],
    path: '/forms/new/event',
    permission: 'org:forms:process_forms',
  },
  {
    name: 'open_create_element',
    description: 'Open the create element workspace for embeddable widgets.',
    examples: ['create an element', 'make an embed widget', 'add a donation widget'],
    path: '/elements/new',
    permission: 'org:elements:process_elements',
  },
  {
    name: 'open_profile_settings',
    description: 'Open the profile tab inside account settings.',
    examples: ['open my profile', 'show profile settings', 'go to my account profile'],
    path: '/settings/organization/profile',
  },
  {
    name: 'open_billing_settings',
    description: 'Open the billing tab inside account settings.',
    examples: ['open billing', 'show billing settings', 'go to subscription billing'],
    path: '/settings/billing',
    permission: 'org:billing:manage_billing',
  },
  {
    name: 'open_users_settings',
    description: 'Open organization user management settings.',
    examples: ['open users', 'manage team members', 'show user settings'],
    path: '/settings/organization/users',
    permission: 'org:settings:manage_organization_settings',
  },
  {
    name: 'open_permissions_settings',
    description: 'Open organization role and permission settings.',
    examples: ['open permissions', 'manage roles', 'show role access'],
    path: '/settings/organization/permissions',
    permission: 'org:settings:manage_organization_settings',
  },
  {
    name: 'open_embed_settings',
    description: 'Open checkout embed settings and script instructions.',
    examples: ['open embed settings', 'show the embed script', 'go to checkout script'],
    path: '/settings/embed',
    permission: 'org:settings:manage_checkout_settings',
  },
  {
    name: 'open_faq_settings',
    description: 'Open checkout FAQ settings.',
    examples: ['open FAQs', 'edit checkout FAQs', 'show FAQ settings'],
    path: '/settings/faqs',
    permission: 'org:settings:manage_checkout_settings',
  },
  {
    name: 'open_custom_input_settings',
    description: 'Open custom input field settings for checkout forms.',
    examples: ['open custom inputs', 'show custom fields', 'edit checkout questions'],
    path: '/settings/custom-input-fields',
    permission: 'org:settings:manage_checkout_settings',
  },
  {
    name: 'open_email_settings',
    description: 'Open email and receipt settings.',
    examples: ['open email settings', 'show receipt settings', 'configure emails'],
    path: '/settings/emails',
    permission: 'org:settings:manage_email_settings',
  },
  {
    name: 'open_compliance_settings',
    description: 'Open compliance settings.',
    examples: ['open compliance', 'show PCI compliance', 'go to compliance settings'],
    path: '/settings/compliance',
    permission: 'org:settings:manage_processor_settings',
  },
  {
    name: 'open_payout_frequency_settings',
    description: 'Open payout frequency settings.',
    examples: ['open payout frequency', 'change payout schedule', 'show payout timing'],
    path: '/settings/payout-frequency',
    permission: 'org:payouts:manage_payouts',
  },
  {
    name: 'open_integrations_settings',
    description: 'Open integrations settings for CRMs, payment processors, and connected apps.',
    examples: ['open integrations', 'show CRM integrations', 'go to connected apps'],
    path: '/settings/integrations',
    permission: 'org:settings:manage_integration_settings',
  },
  {
    name: 'open_contact_portal_settings',
    description: 'Open the Contact Portal element on the Elements page.',
    examples: [
      'open contact portal',
      'manage the contact portal',
      'go to the contact portal element',
    ],
    path: '/elements',
    permission: 'org:settings:manage_checkout_settings',
  },
] as const satisfies readonly DashboardAssistantNavigationTarget[];

export function getDashboardAssistantNavigationTarget(name: string) {
  return dashboardAssistantNavigationTargets.find((target) => target.name === name);
}
